import { ApplicationConfig, provideBrowserGlobalErrorListeners, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideModusWebComponents } from '@trimble-oss/moduswebcomponents-angular';
import { provideOAuthClient, OAuthService } from 'angular-oauth2-oidc';
import { routes } from './app.routes';
import { projectsReducer } from './state/projects/projects.reducer';
import { ProjectsEffects } from './state/projects/projects.effects';
import { floorsReducer } from './state/floors/floors.reducer';
import { FloorsEffects } from './state/floors/floors.effects';
import { drawingsReducer } from './state/drawings/drawings.reducer';
import { DrawingsEffects } from './state/drawings/drawings.effects';
import { captureSessionsReducer } from './state/capture-sessions/capture-sessions.reducer';
import { CaptureSessionsEffects } from './state/capture-sessions/capture-sessions.effects';
import { capturesReducer } from './state/captures/captures.reducer';
import { CapturesEffects } from './state/captures/captures.effects';
import { processingResultsReducer } from './state/processing-results/processing-results.reducer';
import { ProcessingResultsEffects } from './state/processing-results/processing-results.effects';
import { AuthTokenInterceptor } from './interceptors/auth-token.interceptor';
import { BffSessionRefreshInterceptor } from './interceptors/bff-session-refresh.interceptor';
import { ApiAuthRefreshInterceptor } from './interceptors/api-auth-refresh.interceptor';
import { AuthService } from './services/auth/auth.service';
import { AuthMockService } from './services/auth/auth.mock.service';
import { AuthServiceBase } from './services/auth/auth.service.base';
import { PROJECTS_SERVICE } from './services/projects/projects.service.base';
import { ProjectsService } from './services/projects/projects.service';
import { ProjectsMockService } from './services/projects/projects-mock.service';
import { FLOORS_SERVICE } from './services/floors/floors.service.base';
import { FloorsService } from './services/floors/floors.service';
import { FloorsMockService } from './services/floors/floors-mock.service';
import { DRAWINGS_SERVICE } from './services/drawings/drawings.service.base';
import { DrawingsService } from './services/drawings/drawings.service';
import { DrawingsMockService } from './services/drawings/drawings-mock.service';
import { CAPTURE_SESSIONS_SERVICE } from './services/capture-sessions/capture-sessions.service.base';
import { CaptureSessionsService } from './services/capture-sessions/capture-sessions.service';
import { CaptureSessionsMockService } from './services/capture-sessions/capture-sessions-mock.service';
import { CAPTURES_SERVICE } from './services/captures/captures.service.base';
import { CapturesService } from './services/captures/captures.service';
import { CapturesMockService } from './services/captures/captures-mock.service';
import { PROCESSING_RESULTS_SERVICE } from './services/processing-results/processing-results.service.base';
import { ProcessingResultsService } from './services/processing-results/processing-results.service';
import { ProcessingResultsMockService } from './services/processing-results/processing-results-mock.service';
import { environment } from './environments/environment';
import { authInit } from './configs/authentication-config-builder';
import { USER_ACCESS_SERVICE } from './services/user-access/user-access.service.base';
import { UserAccessService } from './services/user-access/user-access.service';
import { UserAccessMockService } from './services/user-access/user-access-mock.service';
import { REGISTERED_APPLICATIONS_SERVICE } from './services/registered-applications/registered-applications.service.base';
import { RegisteredApplicationsService } from './services/registered-applications/registered-applications.service';
import { RegisteredApplicationsMockService } from './services/registered-applications/registered-applications-mock.service';
import { ADMIN_USERS_SERVICE } from './services/admin-users/admin-users.service.base';
import { AdminUsersService } from './services/admin-users/admin-users.service';
import { AdminUsersMockService } from './services/admin-users/admin-users-mock.service';
import { ScitBffSessionKeepAliveService } from './services/viewer/scit-bff-session-keep-alive.service';
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideModusWebComponents(),
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    provideOAuthClient(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BffSessionRefreshInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiAuthRefreshInterceptor,
      multi: true,
    },
    {
      provide: AuthServiceBase,
      useClass: environment.mock ? AuthMockService : AuthService,
    },
    {
      provide: PROJECTS_SERVICE,
      useClass: environment.mock ? ProjectsMockService : ProjectsService,
    },
    {
      provide: FLOORS_SERVICE,
      useClass: environment.mock ? FloorsMockService : FloorsService,
    },
    {
      provide: DRAWINGS_SERVICE,
      useClass: environment.mock ? DrawingsMockService : DrawingsService,
    },
    {
      provide: CAPTURE_SESSIONS_SERVICE,
      useClass: environment.mock ? CaptureSessionsMockService : CaptureSessionsService,
    },
    {
      provide: CAPTURES_SERVICE,
      useClass: environment.mock ? CapturesMockService : CapturesService,
    },
    {
      provide: PROCESSING_RESULTS_SERVICE,
      useClass: environment.mock ? ProcessingResultsMockService : ProcessingResultsService,
    },
    {
      provide: USER_ACCESS_SERVICE,
      useClass: environment.mock ? UserAccessMockService : UserAccessService,
    },
    {
      provide: REGISTERED_APPLICATIONS_SERVICE,
      useClass: environment.mock ? RegisteredApplicationsMockService : RegisteredApplicationsService,
    },
    {
      provide: ADMIN_USERS_SERVICE,
      useClass: environment.mock ? AdminUsersMockService : AdminUsersService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authInit,
      deps: [OAuthService, ScitBffSessionKeepAliveService],
      multi: true,
    },
    provideStore({
      projects: projectsReducer,
      floors: floorsReducer,
      drawings: drawingsReducer,
      captureSessions: captureSessionsReducer,
      captures: capturesReducer,
      processingResults: processingResultsReducer,
    }),
    provideEffects([
      ProjectsEffects,
      FloorsEffects,
      DrawingsEffects,
      CaptureSessionsEffects,
      CapturesEffects,
      ProcessingResultsEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
    }),
  ],
};
